class Stroke {
    constructor(squareSize, nbSamplePoints, points, name) {
        this.squareSize = squareSize;
        this.nbSamplePoints = nbSamplePoints;
        this.points = points;
        this.name = name;
        this.processStroke();
    }

    getDistance(a, b) {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        return Math.hypot(dx, dy);
    }

    processStroke() {
        this.points = this.resample();
        this.setCentroid();
        this.points = this.rotateBy(-this.indicativeAngle());
        this.points = this.scaleToSquare();
        this.setCentroid();
        this.points = this.translateToOrigin();
        return this;
    }

    resample() {
        const interval = this.strokeLength() / (this.nbSamplePoints - 1);
        const newPoints = [this.points[0]];
        let distance = 0;

        for (let i = 1; i < this.points.length; i++) {
            let localDistance = this.getDistance(this.points[i - 1], this.points[i]);

            if (distance + localDistance >= interval) {
                let q = {
                    x: this.points[i - 1].x + ((interval - distance) / localDistance) * (this.points[i].x - this.points[i - 1].x),
                    y: this.points[i - 1].y + ((interval - distance) / localDistance) * (this.points[i].y - this.points[i - 1].y)
                };

                newPoints.push(q);
                this.points.splice(i, 0, q);
                distance = 0;
            } else {
                distance += localDistance;
            }
        }

        if (newPoints.length === this.nbSamplePoints - 1) {
            newPoints.push(this.points[this.points.length - 1]);
        }

        return newPoints;
    }

    rotateBy(angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return this.points.map(point => {
            return {
                x: (point.x - this.c.x) * cos - (point.y - this.c.y) * sin + this.c.x,
                y: (point.x - this.c.x) * sin + (point.y - this.c.y) * cos + this.c.y
            };
        });
    }

    scaleToSquare() {
        const box = {
            minX: +Infinity,
            maxX: -Infinity,
            minY: +Infinity,
            maxY: -Infinity
        };

        for (let point of this.points) {
            box.minX = Math.min(box.minX, point.x);
            box.minY = Math.min(box.minY, point.y);
            box.maxX = Math.max(box.maxX, point.x);
            box.maxY = Math.max(box.maxY, point.y);
        }
        box.width = box.maxX - box.minX;
        box.height = box.maxY - box.minY;

        return this.points.map(point => {
            return {
                x: point.x * (this.squareSize / box.width),
                y: point.y * (this.squareSize / box.height)
            };
        });
    }

    translateToOrigin() {
        const _origin = {
            x: 0,
            y: 0
        };
        return this.points.map(point => {
            return {
                x: point.x + _origin.x - this.c.x,
                y: point.y + _origin.y - this.c.y
            };
        });
    }

    setCentroid() {
        this.c = this.points.reduce((acc, point) => {
            acc.x += point.x;
            acc.y += point.y;
            return acc;
        }, {
            x: 0,
            y: 0
        });
        this.c.x /= this.points.length;
        this.c.y /= this.points.length;
    }

    indicativeAngle() {
        return Math.atan2(this.c.y - this.points[0].y, this.c.x - this.points[0].x);
    }

    strokeLength() {
        return this.points.reduce((acc, point, i, arr) => {
            if (i > 0) {
                acc += this.getDistance(arr[i - 1], point);
            }
            return acc;
        }, 0);
    }

    distanceAtBestAngle(pattern) {
        const _phi = 0.5 * (-1 + Math.sqrt(5));
        const deg2Rad = d => d * Math.PI / 180;
        const _angleRange = deg2Rad(45);
        const _anglePrecision = deg2Rad(2);
        let a = -_angleRange;
        let b = _angleRange;
        let x1 = _phi * a + (1 - _phi) * b;
        let f1 = this.distanceAtAngle(pattern, x1);
        let x2 = (1 - _phi) * a + _phi * b;
        let f2 = this.distanceAtAngle(pattern, x2);

        while (Math.abs(b - a) > _anglePrecision) {
            if (f1 < f2) {
                b = x2;
                x2 = x1;
                f2 = f1;
                x1 = _phi * a + (1 - _phi) * b;
                f1 = this.distanceAtAngle(pattern, x1);
            } else {
                a = x1;
                x1 = x2;
                f1 = f2;
                x2 = (1 - _phi) * a + _phi * b;
                f2 = this.distanceAtAngle(pattern, x2);
            }
        }
        return Math.min(f1, f2);
    }

    distanceAtAngle(pattern, angle) {
        const strokePoints = this.rotateBy(angle);
        const d = strokePoints.reduce((acc, point, i) => {
            acc += this.getDistance(point, pattern.points[i]);
            return acc;
        }, 0);
        return d / strokePoints.length;
    }

}

class ShapeDetector {
    constructor() {
        const defaultShapes = [{
                points: [{
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 367.95938301086414,
                    y: 315.39375686645496
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 367.95938301086414,
                    y: 315.39375686645496
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 367.95938301086414,
                    y: 315.39375686645496
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 367.9593830108641,
                    y: 315.39375686645496
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 367.9593830108641,
                    y: 315.39375686645496
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 262.8281307220458,
                    y: 175.2187538146972
                }, {
                    x: 245.30625534057606,
                    y: 210.26250457763663
                }, {
                    x: 227.78437995910636,
                    y: 245.30625534057606
                }, {
                    x: 210.26250457763663,
                    y: 280.3500061035155
                }, {
                    x: 192.7406291961669,
                    y: 315.39375686645496
                }, {
                    x: 175.2187538146972,
                    y: 350.4375076293944
                }, {
                    x: 157.69687843322748,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 403.00313377380354,
                    y: 385.4812583923338
                }, {
                    x: 385.4812583923338,
                    y: 350.4375076293944
                }, {
                    x: 367.9593830108641,
                    y: 315.39375686645496
                }, {
                    x: 350.4375076293944,
                    y: 280.3500061035155
                }, {
                    x: 332.9156322479247,
                    y: 245.30625534057606
                }, {
                    x: 315.39375686645496,
                    y: 210.26250457763663
                }, {
                    x: 297.87188148498524,
                    y: 175.2187538146972
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }],
                name: 'triangle'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 175.2187538146972
                }, {
                    x: 140.17500305175776,
                    y: 210.26250457763663
                }, {
                    x: 140.17500305175776,
                    y: 245.30625534057606
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 140.17500305175776,
                    y: 315.39375686645496
                }, {
                    x: 140.17500305175776,
                    y: 350.4375076293944
                }, {
                    x: 140.17500305175776,
                    y: 385.4812583923338
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 140.17500305175776,
                    y: 420.52500915527327
                }, {
                    x: 175.2187538146972,
                    y: 420.52500915527327
                }, {
                    x: 210.26250457763663,
                    y: 420.52500915527327
                }, {
                    x: 245.30625534057606,
                    y: 420.52500915527327
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 315.39375686645496,
                    y: 420.52500915527327
                }, {
                    x: 350.4375076293944,
                    y: 420.52500915527327
                }, {
                    x: 385.4812583923338,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 420.52500915527327
                }, {
                    x: 420.52500915527327,
                    y: 385.4812583923338
                }, {
                    x: 420.52500915527327,
                    y: 350.4375076293944
                }, {
                    x: 420.52500915527327,
                    y: 315.39375686645496
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 420.52500915527327,
                    y: 245.30625534057606
                }, {
                    x: 420.52500915527327,
                    y: 210.26250457763663
                }, {
                    x: 420.52500915527327,
                    y: 175.2187538146972
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 420.52500915527327,
                    y: 140.17500305175776
                }, {
                    x: 385.4812583923338,
                    y: 140.17500305175776
                }, {
                    x: 350.4375076293944,
                    y: 140.17500305175776
                }, {
                    x: 315.39375686645496,
                    y: 140.17500305175776
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 245.30625534057606,
                    y: 140.17500305175776
                }, {
                    x: 210.26250457763663,
                    y: 140.17500305175776
                }, {
                    x: 175.2187538146972,
                    y: 140.17500305175776
                }, {
                    x: 140.17500305175776,
                    y: 140.17500305175776
                }],
                name: 'square'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 304.69113993790967
                }, {
                    x: 412.07142208989444,
                    y: 328.29268073795373
                }, {
                    x: 401.74511972189896,
                    y: 350.43750762939436
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 350.4375076293944,
                    y: 401.74511972189896
                }, {
                    x: 328.2926807379538,
                    y: 412.07142208989444
                }, {
                    x: 304.69113993790967,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 256.0088722691214,
                    y: 418.3954358873965
                }, {
                    x: 232.4073314690773,
                    y: 412.07142208989444
                }, {
                    x: 210.26250457763666,
                    y: 401.74511972189896
                }, {
                    x: 190.2472509540728,
                    y: 387.73028825550034
                }, {
                    x: 172.9697239515307,
                    y: 370.4527612529582
                }, {
                    x: 158.95489248513206,
                    y: 350.43750762939436
                }, {
                    x: 148.62859011713658,
                    y: 328.2926807379538
                }, {
                    x: 142.30457631963455,
                    y: 304.6911399379096
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 256.00887226912135
                }, {
                    x: 148.62859011713655,
                    y: 232.4073314690773
                }, {
                    x: 158.9548924851321,
                    y: 210.2625045776366
                }, {
                    x: 172.96972395153068,
                    y: 190.2472509540728
                }, {
                    x: 190.24725095407277,
                    y: 172.9697239515307
                }, {
                    x: 210.26250457763658,
                    y: 158.95489248513212
                }, {
                    x: 232.40733146907718,
                    y: 148.62859011713658
                }, {
                    x: 256.00887226912135,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 304.6911399379096,
                    y: 142.30457631963455
                }, {
                    x: 328.2926807379537,
                    y: 148.62859011713653
                }, {
                    x: 350.4375076293944,
                    y: 158.9548924851321
                }, {
                    x: 370.4527612529582,
                    y: 172.96972395153068
                }, {
                    x: 387.73028825550034,
                    y: 190.24725095407274
                }, {
                    x: 401.7451197218989,
                    y: 210.26250457763658
                }, {
                    x: 412.07142208989444,
                    y: 232.4073314690773
                }, {
                    x: 418.39543588739645,
                    y: 256.00887226912124
                }, {
                    x: 420.52500915527327,
                    y: 280.35000610351545
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 256.00887226912135
                }, {
                    x: 412.07142208989444,
                    y: 232.4073314690773
                }, {
                    x: 401.74511972189896,
                    y: 210.26250457763666
                }, {
                    x: 387.73028825550034,
                    y: 190.2472509540728
                }, {
                    x: 370.4527612529582,
                    y: 172.96972395153068
                }, {
                    x: 350.4375076293944,
                    y: 158.9548924851321
                }, {
                    x: 328.2926807379538,
                    y: 148.62859011713658
                }, {
                    x: 304.69113993790967,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 256.0088722691214,
                    y: 142.30457631963455
                }, {
                    x: 232.4073314690773,
                    y: 148.62859011713655
                }, {
                    x: 210.26250457763666,
                    y: 158.95489248513206
                }, {
                    x: 190.2472509540728,
                    y: 172.96972395153068
                }, {
                    x: 172.9697239515307,
                    y: 190.24725095407277
                }, {
                    x: 158.95489248513206,
                    y: 210.26250457763666
                }, {
                    x: 148.62859011713658,
                    y: 232.40733146907723
                }, {
                    x: 142.30457631963455,
                    y: 256.0088722691214
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 304.69113993790967
                }, {
                    x: 148.62859011713655,
                    y: 328.29268073795373
                }, {
                    x: 158.9548924851321,
                    y: 350.4375076293944
                }, {
                    x: 172.96972395153068,
                    y: 370.4527612529582
                }, {
                    x: 190.24725095407277,
                    y: 387.73028825550034
                }, {
                    x: 210.26250457763658,
                    y: 401.7451197218989
                }, {
                    x: 232.40733146907718,
                    y: 412.07142208989444
                }, {
                    x: 256.00887226912135,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 304.6911399379096,
                    y: 418.3954358873965
                }, {
                    x: 328.2926807379537,
                    y: 412.0714220898945
                }, {
                    x: 350.4375076293944,
                    y: 401.74511972189896
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529583
                }, {
                    x: 401.7451197218989,
                    y: 350.4375076293944
                }, {
                    x: 412.07142208989444,
                    y: 328.29268073795373
                }, {
                    x: 418.39543588739645,
                    y: 304.6911399379098
                }, {
                    x: 420.52500915527327,
                    y: 280.35000610351557
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 256.00887226912135
                }, {
                    x: 148.62859011713655,
                    y: 232.4073314690773
                }, {
                    x: 158.95489248513206,
                    y: 210.26250457763666
                }, {
                    x: 172.96972395153068,
                    y: 190.2472509540728
                }, {
                    x: 190.2472509540728,
                    y: 172.96972395153068
                }, {
                    x: 210.2625045776366,
                    y: 158.9548924851321
                }, {
                    x: 232.40733146907726,
                    y: 148.62859011713658
                }, {
                    x: 256.00887226912135,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 304.6911399379096,
                    y: 142.30457631963455
                }, {
                    x: 328.29268073795373,
                    y: 148.62859011713655
                }, {
                    x: 350.43750762939436,
                    y: 158.95489248513206
                }, {
                    x: 370.4527612529582,
                    y: 172.96972395153068
                }, {
                    x: 387.73028825550034,
                    y: 190.24725095407277
                }, {
                    x: 401.74511972189896,
                    y: 210.26250457763666
                }, {
                    x: 412.07142208989444,
                    y: 232.40733146907723
                }, {
                    x: 418.3954358873965,
                    y: 256.0088722691214
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 304.69113993790967
                }, {
                    x: 412.07142208989444,
                    y: 328.29268073795373
                }, {
                    x: 401.74511972189896,
                    y: 350.4375076293944
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 350.4375076293944,
                    y: 401.7451197218989
                }, {
                    x: 328.29268073795384,
                    y: 412.07142208989444
                }, {
                    x: 304.69113993790967,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 256.0088722691214,
                    y: 418.3954358873965
                }, {
                    x: 232.40733146907735,
                    y: 412.0714220898945
                }, {
                    x: 210.2625045776366,
                    y: 401.74511972189896
                }, {
                    x: 190.2472509540728,
                    y: 387.73028825550034
                }, {
                    x: 172.9697239515307,
                    y: 370.4527612529583
                }, {
                    x: 158.95489248513212,
                    y: 350.4375076293944
                }, {
                    x: 148.62859011713655,
                    y: 328.29268073795373
                }, {
                    x: 142.30457631963458,
                    y: 304.6911399379098
                }, {
                    x: 140.17500305175776,
                    y: 280.35000610351557
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 304.69113993790967
                }, {
                    x: 148.62859011713655,
                    y: 328.29268073795373
                }, {
                    x: 158.95489248513206,
                    y: 350.43750762939436
                }, {
                    x: 172.96972395153068,
                    y: 370.4527612529582
                }, {
                    x: 190.2472509540728,
                    y: 387.73028825550034
                }, {
                    x: 210.2625045776366,
                    y: 401.74511972189896
                }, {
                    x: 232.40733146907726,
                    y: 412.07142208989444
                }, {
                    x: 256.00887226912135,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 304.6911399379096,
                    y: 418.3954358873965
                }, {
                    x: 328.29268073795373,
                    y: 412.07142208989444
                }, {
                    x: 350.43750762939436,
                    y: 401.74511972189896
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 401.74511972189896,
                    y: 350.43750762939436
                }, {
                    x: 412.07142208989444,
                    y: 328.2926807379538
                }, {
                    x: 418.3954358873965,
                    y: 304.6911399379096
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 256.00887226912135
                }, {
                    x: 412.07142208989444,
                    y: 232.4073314690773
                }, {
                    x: 401.74511972189896,
                    y: 210.2625045776366
                }, {
                    x: 387.73028825550034,
                    y: 190.2472509540728
                }, {
                    x: 370.4527612529582,
                    y: 172.9697239515307
                }, {
                    x: 350.4375076293944,
                    y: 158.95489248513212
                }, {
                    x: 328.29268073795384,
                    y: 148.62859011713658
                }, {
                    x: 304.69113993790967,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 256.0088722691214,
                    y: 142.30457631963455
                }, {
                    x: 232.40733146907735,
                    y: 148.62859011713653
                }, {
                    x: 210.2625045776366,
                    y: 158.9548924851321
                }, {
                    x: 190.2472509540728,
                    y: 172.96972395153068
                }, {
                    x: 172.9697239515307,
                    y: 190.24725095407274
                }, {
                    x: 158.95489248513212,
                    y: 210.26250457763658
                }, {
                    x: 148.62859011713655,
                    y: 232.4073314690773
                }, {
                    x: 142.30457631963458,
                    y: 256.00887226912124
                }, {
                    x: 140.17500305175776,
                    y: 280.35000610351545
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 304.6911399379096,
                    y: 418.3954358873965
                }, {
                    x: 328.29268073795373,
                    y: 412.07142208989444
                }, {
                    x: 350.43750762939436,
                    y: 401.74511972189896
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 401.74511972189896,
                    y: 350.43750762939436
                }, {
                    x: 412.07142208989444,
                    y: 328.2926807379538
                }, {
                    x: 418.3954358873965,
                    y: 304.6911399379096
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 256.00887226912135
                }, {
                    x: 412.07142208989444,
                    y: 232.4073314690773
                }, {
                    x: 401.74511972189896,
                    y: 210.2625045776366
                }, {
                    x: 387.73028825550034,
                    y: 190.2472509540728
                }, {
                    x: 370.4527612529582,
                    y: 172.9697239515307
                }, {
                    x: 350.4375076293944,
                    y: 158.95489248513212
                }, {
                    x: 328.29268073795384,
                    y: 148.62859011713658
                }, {
                    x: 304.69113993790967,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 256.0088722691214,
                    y: 142.30457631963455
                }, {
                    x: 232.40733146907735,
                    y: 148.62859011713653
                }, {
                    x: 210.2625045776366,
                    y: 158.9548924851321
                }, {
                    x: 190.2472509540728,
                    y: 172.96972395153068
                }, {
                    x: 172.9697239515307,
                    y: 190.24725095407274
                }, {
                    x: 158.95489248513212,
                    y: 210.26250457763658
                }, {
                    x: 148.62859011713655,
                    y: 232.4073314690773
                }, {
                    x: 142.30457631963458,
                    y: 256.00887226912124
                }, {
                    x: 140.17500305175776,
                    y: 280.35000610351545
                }, {
                    x: 142.30457631963455,
                    y: 304.6911399379096
                }, {
                    x: 148.62859011713658,
                    y: 328.2926807379538
                }, {
                    x: 158.954892485132,
                    y: 350.4375076293943
                }, {
                    x: 172.96972395153068,
                    y: 370.4527612529582
                }, {
                    x: 190.24725095407274,
                    y: 387.7302882555003
                }, {
                    x: 210.26250457763666,
                    y: 401.74511972189896
                }, {
                    x: 232.40733146907718,
                    y: 412.07142208989444
                }, {
                    x: 256.00887226912135,
                    y: 418.3954358873965
                }, {
                    x: 280.35000610351545,
                    y: 420.52500915527327
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 304.6911399379096,
                    y: 142.30457631963455
                }, {
                    x: 328.29268073795373,
                    y: 148.62859011713655
                }, {
                    x: 350.43750762939436,
                    y: 158.95489248513206
                }, {
                    x: 370.4527612529582,
                    y: 172.96972395153068
                }, {
                    x: 387.73028825550034,
                    y: 190.24725095407277
                }, {
                    x: 401.74511972189896,
                    y: 210.26250457763666
                }, {
                    x: 412.07142208989444,
                    y: 232.40733146907723
                }, {
                    x: 418.3954358873965,
                    y: 256.0088722691214
                }, {
                    x: 420.52500915527327,
                    y: 280.3500061035155
                }, {
                    x: 418.3954358873965,
                    y: 304.69113993790967
                }, {
                    x: 412.07142208989444,
                    y: 328.29268073795373
                }, {
                    x: 401.74511972189896,
                    y: 350.4375076293944
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 350.4375076293944,
                    y: 401.7451197218989
                }, {
                    x: 328.29268073795384,
                    y: 412.07142208989444
                }, {
                    x: 304.69113993790967,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 256.0088722691214,
                    y: 418.3954358873965
                }, {
                    x: 232.40733146907735,
                    y: 412.0714220898945
                }, {
                    x: 210.2625045776366,
                    y: 401.74511972189896
                }, {
                    x: 190.2472509540728,
                    y: 387.73028825550034
                }, {
                    x: 172.9697239515307,
                    y: 370.4527612529583
                }, {
                    x: 158.95489248513212,
                    y: 350.4375076293944
                }, {
                    x: 148.62859011713655,
                    y: 328.29268073795373
                }, {
                    x: 142.30457631963458,
                    y: 304.6911399379098
                }, {
                    x: 140.17500305175776,
                    y: 280.35000610351557
                }, {
                    x: 142.30457631963455,
                    y: 256.0088722691214
                }, {
                    x: 148.62859011713658,
                    y: 232.40733146907723
                }, {
                    x: 158.954892485132,
                    y: 210.26250457763672
                }, {
                    x: 172.96972395153068,
                    y: 190.2472509540728
                }, {
                    x: 190.24725095407274,
                    y: 172.96972395153074
                }, {
                    x: 210.26250457763666,
                    y: 158.95489248513206
                }, {
                    x: 232.40733146907718,
                    y: 148.6285901171366
                }, {
                    x: 256.00887226912135,
                    y: 142.30457631963455
                }, {
                    x: 280.35000610351545,
                    y: 140.17500305175776
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 256.0088722691214,
                    y: 142.30457631963455
                }, {
                    x: 232.4073314690773,
                    y: 148.62859011713655
                }, {
                    x: 210.26250457763666,
                    y: 158.95489248513206
                }, {
                    x: 190.2472509540728,
                    y: 172.96972395153068
                }, {
                    x: 172.9697239515307,
                    y: 190.24725095407277
                }, {
                    x: 158.95489248513206,
                    y: 210.26250457763666
                }, {
                    x: 148.62859011713658,
                    y: 232.40733146907723
                }, {
                    x: 142.30457631963455,
                    y: 256.0088722691214
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 304.69113993790967
                }, {
                    x: 148.62859011713655,
                    y: 328.29268073795373
                }, {
                    x: 158.9548924851321,
                    y: 350.4375076293944
                }, {
                    x: 172.96972395153068,
                    y: 370.4527612529582
                }, {
                    x: 190.24725095407277,
                    y: 387.73028825550034
                }, {
                    x: 210.26250457763658,
                    y: 401.7451197218989
                }, {
                    x: 232.40733146907718,
                    y: 412.07142208989444
                }, {
                    x: 256.00887226912135,
                    y: 418.3954358873965
                }, {
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 304.6911399379096,
                    y: 418.3954358873965
                }, {
                    x: 328.2926807379537,
                    y: 412.0714220898945
                }, {
                    x: 350.4375076293944,
                    y: 401.74511972189896
                }, {
                    x: 370.4527612529582,
                    y: 387.73028825550034
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529583
                }, {
                    x: 401.7451197218989,
                    y: 350.4375076293944
                }, {
                    x: 412.07142208989444,
                    y: 328.29268073795373
                }, {
                    x: 418.39543588739645,
                    y: 304.6911399379098
                }, {
                    x: 420.52500915527327,
                    y: 280.35000610351557
                }, {
                    x: 418.3954358873965,
                    y: 256.0088722691214
                }, {
                    x: 412.07142208989444,
                    y: 232.40733146907723
                }, {
                    x: 401.745119721899,
                    y: 210.26250457763672
                }, {
                    x: 387.73028825550034,
                    y: 190.2472509540728
                }, {
                    x: 370.4527612529583,
                    y: 172.96972395153074
                }, {
                    x: 350.43750762939436,
                    y: 158.95489248513206
                }, {
                    x: 328.29268073795384,
                    y: 148.6285901171366
                }, {
                    x: 304.69113993790967,
                    y: 142.30457631963455
                }, {
                    x: 280.35000610351557,
                    y: 140.17500305175776
                }],
                name: 'circle'
            },
            {
                points: [{
                    x: 280.3500061035155,
                    y: 420.52500915527327
                }, {
                    x: 256.0088722691214,
                    y: 418.3954358873965
                }, {
                    x: 232.4073314690773,
                    y: 412.07142208989444
                }, {
                    x: 210.26250457763666,
                    y: 401.74511972189896
                }, {
                    x: 190.2472509540728,
                    y: 387.73028825550034
                }, {
                    x: 172.9697239515307,
                    y: 370.4527612529582
                }, {
                    x: 158.95489248513206,
                    y: 350.43750762939436
                }, {
                    x: 148.62859011713658,
                    y: 328.2926807379538
                }, {
                    x: 142.30457631963455,
                    y: 304.6911399379096
                }, {
                    x: 140.17500305175776,
                    y: 280.3500061035155
                }, {
                    x: 142.30457631963455,
                    y: 256.00887226912135
                }, {
                    x: 148.62859011713655,
                    y: 232.4073314690773
                }, {
                    x: 158.9548924851321,
                    y: 210.2625045776366
                }, {
                    x: 172.96972395153068,
                    y: 190.2472509540728
                }, {
                    x: 190.24725095407277,
                    y: 172.9697239515307
                }, {
                    x: 210.26250457763658,
                    y: 158.95489248513212
                }, {
                    x: 232.40733146907718,
                    y: 148.62859011713658
                }, {
                    x: 256.00887226912135,
                    y: 142.30457631963455
                }, {
                    x: 280.3500061035155,
                    y: 140.17500305175776
                }, {
                    x: 304.6911399379096,
                    y: 142.30457631963455
                }, {
                    x: 328.2926807379537,
                    y: 148.62859011713653
                }, {
                    x: 350.4375076293944,
                    y: 158.9548924851321
                }, {
                    x: 370.4527612529582,
                    y: 172.96972395153068
                }, {
                    x: 387.73028825550034,
                    y: 190.24725095407274
                }, {
                    x: 401.7451197218989,
                    y: 210.26250457763658
                }, {
                    x: 412.07142208989444,
                    y: 232.4073314690773
                }, {
                    x: 418.39543588739645,
                    y: 256.00887226912124
                }, {
                    x: 420.52500915527327,
                    y: 280.35000610351545
                }, {
                    x: 418.3954358873965,
                    y: 304.6911399379096
                }, {
                    x: 412.07142208989444,
                    y: 328.2926807379538
                }, {
                    x: 401.745119721899,
                    y: 350.4375076293943
                }, {
                    x: 387.73028825550034,
                    y: 370.4527612529582
                }, {
                    x: 370.4527612529583,
                    y: 387.7302882555003
                }, {
                    x: 350.43750762939436,
                    y: 401.74511972189896
                }, {
                    x: 328.29268073795384,
                    y: 412.07142208989444
                }, {
                    x: 304.69113993790967,
                    y: 418.3954358873965
                }, {
                    x: 280.35000610351557,
                    y: 420.52500915527327
                }],
                name: 'circle'
            }
        ];
        this.threshold = 0.85;
        this.nbSamplePoints = 128;
        this.squareSize = 250;
        this.patterns = defaultShapes.map(shape => {
            let stroke = new Stroke(this.squareSize, this.nbSamplePoints, shape.points, shape.name);
            return stroke;
        });
    }

    spot(points) {
        const halfDiagonal = Math.hypot(this.squareSize, this.squareSize) * 0.5;
        const stroke = new Stroke(this.squareSize, this.nbSamplePoints, points);
        const result = this.patterns.reduce((acc, pattern) => {
            let distance = stroke.distanceAtBestAngle(pattern);
            let score = 1 - distance / halfDiagonal;
            if (distance < acc.bestDistance && score > this.threshold) {
                acc.bestDistance = distance;
                acc.pattern = pattern.name;
            }
            return acc;
        }, {
            pattern: null,
            bestDistance: +Infinity
        });

        return result.pattern;
    }
}

export default ShapeDetector;



// WEBPACK FOOTER //
// ./src/shape.js